import { PreHangOption, PreHangCartOptions, SwingSelects, MortisePrepFile } from "@/types/preHang";
import { PreHangOptions as PhOptions } from "./ph-options";
import { PreHangCache as PhCache } from "./ph-cache";
import { BarnGroove } from "./ph-barn-groove";
import { CustomHingeBoreLocations } from "./ph-hinge-bore";
import { JambsStops } from "./ph-jambs-stops";
import { PrehangStandards } from "./ph-standards";

export class PreHangSelections {
    private Cache: PhCache
    private Options: PhOptions
    private Standards: PrehangStandards
    public JambsStops: JambsStops
    public BarnGroove: BarnGroove
    public CustomHingeBore: CustomHingeBoreLocations

    private activeForm: 'path'|'prehang'|'machine'|'jambs'
    public activeOptions: {[index: string]: any} = {}
    public selections: { [key: string]: any } = {}
    public isContractDoor: boolean
    public useContractOptions = false
    public isFireOrExterior: boolean
    public mortiseFiles: MortisePrepFile[] = []
    public errors: string[] = []
    public isEmpty = true

    public constructor(projectId: number, options: PhOptions, standards: PrehangStandards, isContract: boolean, isBifold: boolean, isFireOrExterior: boolean, path: 'path'|'prehang'|'machine'|'jambs', preHangOptions: PreHangCartOptions | null = null) {
        const cacheKey = `prehang-options-${projectId}${(isFireOrExterior ? '-firext' : '')}`
        this.Cache = new PhCache(cacheKey)
        this.Options = options
        this.Standards = standards
        this.JambsStops = new JambsStops(preHangOptions, isBifold, this.Cache)
        this.BarnGroove = (preHangOptions && preHangOptions.barnGroove) ? new BarnGroove(true, preHangOptions.barnGrooveQuantity,preHangOptions.barnGrooveSize) : new BarnGroove()
        this.CustomHingeBore = new CustomHingeBoreLocations(preHangOptions, this.Cache)
        
        const species = (preHangOptions) ? preHangOptions.jambMaterial : this.Cache.cachedSelections.jambMaterial || ''
        
        this.isContractDoor = isContract
        this.isFireOrExterior = isFireOrExterior
        this.useContractOptions = preHangOptions?.isContract ?? this.isContractDoor
        this.activeForm = this.findActiveForm(path, preHangOptions, isBifold, isFireOrExterior)
        this.activeOptions = this.Options.getOptions(species, this.useContractOptions)
        this.selections = (preHangOptions) ? this.getCartSelections(preHangOptions) : this.getBaseSelections()
    }

    private transferSelections() {
        const keys = Object.keys(this.selections)
        const ignores = this.Standards.getTransferIgnores()
        keys.forEach(key => {
            if(key.toLowerCase().includes('swing')) {
                this.selections[key] = this.transferSwings(key, this.selections[key])
            } else if(!ignores.includes(key)) {
                if(this.selections[key]) {
                    const currentOption = this.selections[key]
                    const newOption = this.activeOptions[key].find((x: PreHangOption) => x.name == currentOption.name)
                    this.selections[key] = newOption
                }
            }
        })
    }

    private transferSwings(type: string, swings: SwingSelects[]) {
        const transfer: SwingSelects[] = []
        const optionField = (type.includes('double')) ? 'doubleDoorSwing' : (type.includes('machine')) ? 'machineOnlySwing' : 'swing'
        swings.forEach((swing: SwingSelects) => {
            const newSwing = this.activeOptions[optionField].find((x: SwingSelects) => x.name == swing.name)
            if(swing.tag) newSwing.tag = swing.tag
            if(swing.quantity) newSwing.quantity = swing.quantity
            newSwing.optionId = newSwing.id
            transfer.push(newSwing)
        })

        return transfer
    }

    public updateSelection(option: PreHangOption, field: string) {
        const defaults = this.Standards.getPrehangDefaults()
        this.selections[field] = option
        this.Cache?.addToCache(field, option.name)
        if(field == 'jambMaterial') {
            this.activeOptions = this.Options.getOptions(option.name, this.useContractOptions)
            this.transferSelections()
        } else if(field == 'bore') {
            const searchTerm = (option.name == 'Mortise Prep') ? 'Mortise Prep' : this.Cache.cachedSelections.latchPrep || defaults.latchPrep
            this.selections.latchPrep = this.activeOptions.latchPrep.find((x: any) => x.name == searchTerm)
        } else if(field == 'machineBore') {
            const searchTerm = (option.name == 'Mortise Prep') ? 'Mortise Prep' : this.Cache.cachedSelections.machineLatchPrep || defaults.machineLatchPrep
            this.selections.machineLatchPrep = this.activeOptions.machineLatchPrep.find((x: any) => x.name == searchTerm)
        } else if(field == 'doubleTAstragal') {
            const searchTerm = (option.name == 'None') ? 'None' : 'Ovolo'
            this.selections.doubleTAstragalStyle = this.activeOptions.doubleTAstragalStyle.find((x: any) => x.name == searchTerm)
        } else if(field == 'machineTAstragal') {
            const searchTerm = (option.name == 'None') ? 'None' : 'Ovolo'
            this.selections.machineTAstragalStyle = this.activeOptions.machineTAstragalStyle.find((x: any) => x.name == searchTerm)
        }
    }

    public updateSwingSelection(swings: SwingSelects[], field: string) {
        this.selections[field] = swings
    }

    private getCartSelections(preHangOptions: PreHangCartOptions) {
        const selections:{[index: string]: any} = {}
        const stringFields = this.Standards.getStringFields()
        
        Object.entries(preHangOptions).forEach(([key,value]) => {
            let option
            if(key.toLowerCase().includes('swing')) { // TODO: MAKE SURE SWING FUNCTIONS CORRECTLY
                option = [...value]
            } else if(key == 'machineHingeThickness') {
                option = {id: 555, name: value, preHangCategoryId: 29, price: 0 }
            } else if(stringFields.includes(key) && key != "customJambWidth") {
                option = this.activeOptions[key].find((x: PreHangOption) => x.name == value)
            } else if(this.activeOptions[key]) {
                option = this.activeOptions[key].find((x: PreHangOption) => x.id == value)
            }

            if(option) selections[key] = option
        })
        return selections
    }

    private getBaseSelections() {
        const defaults = this.Standards.getPrehangDefaults()
        const selections:{[index: string]: PreHangOption} = {}
        const base:{[index: string]: any}  = {
            ...defaults
        }

        const cache:{[index: string]: any} = this.Cache?.cachedSelections || {}
        for(const key in cache) {
            base[key] = cache[key]
        }

        const fields = Object.keys(base)
        fields.forEach(key => {
            let option
            if(key == 'machineHingeThickness') {
                option = {id: 555, name: base[key], preHangCategoryId: 29, price: 0 }
            } else if(this.activeOptions[key]) {
                option = this.activeOptions[key].find((x: PreHangOption) => x.name == base[key])  
            } 
            
            if(option && !selections[key]) {
                selections[key] = option
            }
        })
        
        if(base.bore == 'Mortise Prep') {
            selections.latchPrep = this.activeOptions.latchPrep.find((x: PreHangOption) => x.name == 'Mortise Prep')
        }
        if(base.machineBore == 'Mortise Prep') {
            selections.machineLatchPrep = this.activeOptions.machineLatchPrep.find((x: PreHangOption) => x.name == 'Mortise Prep')
        }
        if(!selections.jambMaterial) {
            selections.jambMaterial = this.activeOptions.jambMaterial[0]
        }
        return selections
    }

    public buildCartOptions() {
        this.isEmpty = true
        const temp: {[key: string]: any} = {}
        const keys = Object.keys(this.selections)
        const stringFields = this.Standards.getStringFields()
        
        keys.forEach(key => {
            if(key == 'machineHingeThickness') {
                temp[key] = parseFloat(this.selections[key].name)
            } else if(stringFields.includes(key)) {
                temp[key] = this.selections[key].name
            } else if(key.toLowerCase().includes('swing')) {
                const swings: SwingSelects[] = []
                this.selections[key].forEach((swing: SwingSelects) => {
                    if(swing.quantity > 0) {
                        swings.push(swing)
                        this.isEmpty = false
                    }
                })
                temp[key] = swings
            } else if(this.selections[key]) {
                temp[key] = this.selections[key].id
            }
        })

        temp.noHingePrep = this.CustomHingeBore.noHingePrep
        if(!temp.noHingePrep) {
            const custom = this.CustomHingeBore.locations
            temp.hingeOne = custom.hingeOne || ''
            temp.hingeTwo = custom.hingeTwo || ''
            temp.hingeThree = custom.hingeThree || ''
            temp.hingeFour = custom.hingeFour || ''
        }
        if(this.selections.machineBore?.name != 'None') {
            const custom = this.CustomHingeBore.locations
            temp.boreOne = (this.selections.machineBore?.name == 'Double') ? custom.boreOne : ''
            temp.boreTwo = custom.boreTwo || ''
        }

        if(this.JambsStops.additionalJambs > 0) {
            temp.additionalJambs = this.JambsStops.additionalJambs
            this.isEmpty = false
        }
        if(this.JambsStops.additionalStops > 0) {
            temp.additionalStops = this.JambsStops.additionalStops
            this.isEmpty = false
        }
        if(this.selections.jambWidth && this.selections.jambWidth.name.toLowerCase() == 'custom' && this.JambsStops.customJambWidth) {
            temp.customJambWidth = this.JambsStops.customJambWidth.name
        }

        temp.barnGroove = this.BarnGroove.isActive
        if(temp.barnGroove) {
            this.isEmpty = false
            temp.barnGrooveQuantity = this.BarnGroove.quantity
            temp.barnGrooveSize = {
                type: this.BarnGroove.type,
                width: this.BarnGroove.width,
                depth: this.BarnGroove.depth
            }
        }

        temp.isContract = this.useContractOptions
        temp.mortisePrep = (this.selections.bore?.name == 'Mortise Prep')
        temp.machineMortisePrep = (this.selections.machineBore?.name == 'Mortise Prep')

        if(temp.mortisePrep || temp.machineMortisePrep) {
            temp.mortisePrepFiles = this.mortiseFiles
        }

        if(!temp.swingQuantities) temp.swingQuantities = []
        if(!temp.doubleSwingQuantities) temp.doubleSwingQuantities = []
        if(!temp.machineSwingQuantities) temp.machineSwingQuantities = []

        this.validate((temp as PreHangCartOptions))
        
        return (this.isEmpty || this.errors.length > 0) ? false : (temp as PreHangCartOptions)

    }

    private validate(cart: PreHangCartOptions) {
        this.errors = []
        // Prehang Checks
        if( (cart.swingQuantities.length > 0 || cart.doubleSwingQuantities.length > 0) && !cart.hingeFinish ) {
            this.errors.push('hingeFinish')
        }

        // Machine Checks
        if(!cart.barnGroove) {
            if(cart.machineSwingQuantities.length > 0) {
                if(!cart.noHingePrep) {
                    if(!cart.machineHingeThickness) this.errors.push('machineHingeThickness')
                    if(!cart.hingeOne) this.errors.push('hingeOne')
                    if(!cart.hingeTwo) this.errors.push('hingeTwo')
                    if(!cart.hingeThree) this.errors.push('hingeThree')
                }
    
                if(this.selections.machineBore?.name == 'Double') {
                    if(!cart.boreOne) this.errors.push('boreOne')
                    if(!cart.boreTwo) this.errors.push('boreTwo')
                } else if(this.selections.machineBore?.name != 'None') {
                    if(!cart.boreTwo) this.errors.push('boreTwo')
                }
            }
        }
    }

    private findActiveForm(path: 'path'|'prehang'|'machine'|'jambs', ph: PreHangCartOptions | null, isBifold = false, isFireOrExterior = false) {
        let form = path
        if(isBifold) {
            form = 'jambs'
        } else if(isFireOrExterior){
            form = 'machine'
        } else if(ph) {
            if(ph.swingQuantities.length > 0 || ph.doubleSwingQuantities.length > 0) {
                form = 'prehang'
            } else if(ph.machineSwingQuantities.length > 0) {
                form = 'machine'
            } else if(ph.additionalJambs > 0 || ph.additionalStops > 0) {
                form = 'jambs'
            }
        }
        return form
    }

    public updateActiveForm(form: 'path'|'prehang'|'machine'|'jambs') {
        this.activeForm = form
    }
    public getActiveForm() {
        return this.activeForm
    }

    public addMortiseFile(file: MortisePrepFile) {
        this.mortiseFiles.push(file)
    }

    public removeMortiseFile(file: MortisePrepFile) {
        const index = this.mortiseFiles.findIndex((x: MortisePrepFile) => x.fileName == file.fileName)
        this.mortiseFiles.splice(index, 1)
    }

    public toggleUseContractOptions() {
        if(this.isContractDoor) {
            this.useContractOptions = !this.useContractOptions
            this.activeOptions = this.Options.getOptions(this.selections.jambMaterial.name, this.useContractOptions)
            this.transferSelections()
        }
    }

}